import { useState, useMemo, type ReactNode } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragStartEvent,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface KanbanColumn {
  id: string;
  title: string;
  color: string; // tailwind bg-color class, e.g. 'bg-emerald-500'
}

export interface KanbanProps<T extends { id: string }> {
  columns: KanbanColumn[];
  items: T[];
  getColumnId: (item: T) => string;
  renderCard: (item: T) => ReactNode;
  onMove: (itemId: string, newColumnId: string) => void;
}

// ---------------------------------------------------------------------------
// Droppable Column wrapper
// ---------------------------------------------------------------------------

function DroppableColumn({
  column,
  count,
  isOver,
  children,
}: {
  column: KanbanColumn;
  count: number;
  isOver: boolean;
  children: ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: column.id });

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[280px] max-w-[320px] flex-1 rounded-xl bg-surface-800 border transition-colors ${
        isOver ? 'border-surface-500 bg-surface-750' : 'border-surface-700'
      }`}
    >
      {/* Column header with colored top border */}
      <div
        className="px-3 py-2.5 rounded-t-xl"
        style={{ borderTop: '2px solid' }}
      >
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${column.color}`} />
          <span className="text-sm font-semibold text-surface-200">
            {column.title}
          </span>
          <span className="ml-auto text-xs text-surface-400 bg-surface-700 px-1.5 py-0.5 rounded-full">
            {count}
          </span>
        </div>
      </div>

      {/* Cards container */}
      <div className="flex-1 p-2 space-y-2 min-h-[200px] overflow-y-auto max-h-[calc(100vh-320px)]">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draggable Card wrapper
// ---------------------------------------------------------------------------

function DraggableCard({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });

  const style = transform
    ? {
        transform: `translate(${transform.x}px, ${transform.y}px)`,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={`rounded-lg bg-surface-700 border border-surface-600 p-3 cursor-grab active:cursor-grabbing transition-shadow ${
        isDragging ? 'opacity-40 shadow-lg' : 'hover:border-surface-500'
      }`}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KanbanBoard
// ---------------------------------------------------------------------------

export function KanbanBoard<T extends { id: string }>({
  columns,
  items,
  getColumnId,
  renderCard,
  onMove,
}: KanbanProps<T>) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  // Group items by column
  const grouped = useMemo(() => {
    const map = new Map<string, T[]>();
    for (const col of columns) {
      map.set(col.id, []);
    }
    for (const item of items) {
      const colId = getColumnId(item);
      const arr = map.get(colId);
      if (arr) arr.push(item);
    }
    return map;
  }, [columns, items, getColumnId]);

  const activeItem = useMemo(
    () => (activeId ? items.find((i) => i.id === activeId) ?? null : null),
    [activeId, items],
  );

  // -- Handlers --

  function handleDragStart(event: DragStartEvent) {
    setActiveId(String(event.active.id));
  }

  function handleDragOver(event: DragOverEvent) {
    const overId = event.over?.id;
    if (!overId) {
      setOverColumnId(null);
      return;
    }
    // overId is either a column id or another card id
    const colId = String(overId);
    if (columns.some((c) => c.id === colId)) {
      setOverColumnId(colId);
    } else {
      // find which column the over-card belongs to
      const overItem = items.find((i) => i.id === colId);
      if (overItem) setOverColumnId(getColumnId(overItem));
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);

    if (!over) return;

    const itemId = String(active.id);
    const overId = String(over.id);

    // Determine target column
    let targetColumnId: string | null = null;

    if (columns.some((c) => c.id === overId)) {
      targetColumnId = overId;
    } else {
      const overItem = items.find((i) => i.id === overId);
      if (overItem) targetColumnId = getColumnId(overItem);
    }

    if (!targetColumnId) return;

    // Only fire if column actually changed
    const currentItem = items.find((i) => i.id === itemId);
    if (!currentItem) return;
    const currentCol = getColumnId(currentItem);
    if (currentCol !== targetColumnId) {
      onMove(itemId, targetColumnId);
    }
  }

  function handleDragCancel() {
    setActiveId(null);
    setOverColumnId(null);
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div className="flex gap-3 overflow-x-auto pb-2">
        {columns.map((col) => {
          const colItems = grouped.get(col.id) ?? [];
          return (
            <DroppableColumn
              key={col.id}
              column={col}
              count={colItems.length}
              isOver={overColumnId === col.id && activeId !== null}
            >
              {colItems.map((item) => (
                <DraggableCard key={item.id} id={item.id}>
                  {renderCard(item)}
                </DraggableCard>
              ))}
            </DroppableColumn>
          );
        })}
      </div>

      {/* Drag overlay — renders a floating copy of the active card */}
      <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
        {activeItem ? (
          <div className="rounded-lg bg-surface-700 border border-nexus-500 p-3 shadow-xl shadow-black/30 rotate-[2deg] cursor-grabbing">
            {renderCard(activeItem)}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
